import { BodySmallBold, Button, H2, Spinner } from '@clubspark-react/clubspark-react-tools'
import { Grid, Hidden } from '@material-ui/core'
import { navigate, RouteComponentProps } from '@reach/router'
import { Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MembershipCodeEnum } from 'src/graphql-types/globalTypes'
import { usePrice } from 'src/hooks/use-price'
import { AssociateInfo } from 'src/types/types'
import { ASSOCIATE_PAYMENT_ROUTE, HOME_ROUTE } from 'src/utils/constants/routes'
import {
  getAssociateInfo,
  getTermId,
  setAssociateInfo,
  setCurrentStep
} from 'src/utils/localStorage/local-storage'
import * as yup from 'yup'
import AddressForm, { Input } from '../address-form/address-form'
import FormErrorMessage from '../form-error-message/form-error-message'
import MembershipSummary from '../membership-summary/membership-summary'
import styles from './associate-details.module.scss'
import { useMembershipTerms } from '../../auth/register/register'

interface AssociateDetailsProps extends RouteComponentProps {}

const AssociateDetails: React.FC<AssociateDetailsProps> = () => {
  const { t } = useTranslation()
  const { hasMultipleActiveTerms } = useMembershipTerms(MembershipCodeEnum.ASCPRO)
  const { amount } = usePrice({
    skip: !getTermId(),
    variables: { input: { membershipCode: MembershipCodeEnum.ASCPRO, termId: getTermId() } }
  })
  const [loading, setLoading] = useState<boolean>(false)
  const associateInfo = getAssociateInfo()

  let initialValues: AssociateInfo = {
    orgName: associateInfo ? associateInfo.orgName : '',
    city: associateInfo ? associateInfo.city : '',
    mailingAddress: associateInfo ? associateInfo.mailingAddress : '',
    state: associateInfo ? associateInfo.state : '',
    zipCode: associateInfo ? associateInfo.zipCode : ''
  }

  const validationSchema = yup.object().shape({
    orgName: yup.string().required(t('is required', { type: 'organization name' })),
    mailingAddress: yup.string().required(t('is required', { type: 'mailing address' })),
    city: yup.string().required(t('is required', { type: 'city' })),
    state: yup.string().required(t('is required', { type: 'state' })),
    zipCode: yup.string().required(t('is required', { type: 'zip code' }))
  })

  const handleBack = () => {
    navigate(HOME_ROUTE)
  }

  const onSubmit = async (values: AssociateInfo, actions) => {
    setLoading(true)
    actions.setSubmitting(true)
    setAssociateInfo(values)
    setLoading(false)
    actions.setSubmitting(false)
    if (!loading) {
      navigate(ASSOCIATE_PAYMENT_ROUTE)
      setCurrentStep(hasMultipleActiveTerms ? '2' : '1')
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => onSubmit(values, actions)}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <Form>
          <Grid container direction="row">
            <Grid item xs={12} sm={12} md={8} lg={6}>
              <Grid container>
                <Grid item xs={12} md={12} lg={12}>
                  <H2 spacing={{ margins: { xxs: 'bottom' } }}>
                    {t('associate/professional details')}
                  </H2>
                </Grid>
                <Grid container>
                  <BodySmallBold spacing={{ margins: { md: 'top', xxs: 'bottom' } }}>
                    {t('organization name')} *
                  </BodySmallBold>
                  <Field name="orgName" component={Input} />
                  {errors?.orgName && touched?.orgName && (
                    <FormErrorMessage message={errors.orgName} />
                  )}
                </Grid>
                <AddressForm
                  errors={errors}
                  isSubmitting={isSubmitting}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  values={values}
                />
                {isSubmitting ? (
                  <Grid container justify="flex-start" className={styles.spinnerContainer}>
                    <Spinner />
                  </Grid>
                ) : (
                  <Grid
                    container
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    className={styles.actionButtonContainer}
                  >
                    <div className={styles.root}>
                      <Button
                        type="button"
                        classname={styles.buttonBack}
                        size="lg"
                        level="secondary"
                        onClick={handleBack}
                      >
                        {t('back')}
                      </Button>
                      <Button type="submit" size="lg" classname={styles.button}>
                        {t('continue')}
                      </Button>
                    </div>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Hidden xsDown>
              <MembershipSummary cost={amount} />
            </Hidden>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
export default AssociateDetails
